import type { Abi, Address } from "viem"
import type { Config } from "wagmi"

import { thickNfpPositionCollectAbi } from "@/domains/abis/thick-v1-nfpm-abi"
import type { TransactionReturnType } from "@/domains/on-chain"
import { simulatedTransaction } from "@/domains/on-chain/simulated-transaction"

interface CollectFeesFromThickNfpPositionProps {
  account: Address
  chainId: number
  config: Config
  tokenId: bigint
  nfpManagerAddress: Address
}

/**
 * Collect fees from a thick-v1 NFP position. The NFPM resolves pool + fee
 * internally from `tokenId`, so the encoder does not need a `ThickV1PoolKey`.
 */
export const collectFeesFromThickNfpPosition = async ({
  account,
  chainId,
  config,
  tokenId,
  nfpManagerAddress,
}: CollectFeesFromThickNfpPositionProps): Promise<TransactionReturnType> => {
  try {
    const params = {
      tokenId,
      recipient: account,
      amount0Max: BigInt(1e36),
      amount1Max: BigInt(1e36),
    }

    return await simulatedTransaction({
      config,
      simulateArgs: {
        abi: thickNfpPositionCollectAbi as Abi,
        account,
        address: nfpManagerAddress,
        args: [params],
        chainId,
        functionName: "collect",
      },
    })
  } catch (error: unknown) {
    console.error("collectFeesFromThickNfpPosition", error)
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
      code: error instanceof Error && "code" in error ? Number((error as { code: string }).code) : 0,
    }
  }
}
